/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationStart, Router, RouterOutlet } from "@angular/router";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { catchError, map, Observable, of, Subject, takeUntil, tap } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { ErrorComponent } from "./pages/error/error.component";
import { FooterComponent } from "./shared/components/footer/footer.component";
import { HeaderComponent } from "./shared/components/header/header.component";
import { SidebarComponent } from "./shared/components/sidebar/sidebar.component";
import { SpinnerComponent } from "./shared/components/spinner/spinner.component";
import { UserInfo } from "./shared/models/http/user/user-info.model";
import { DialogService } from "./shared/services/dialog.service";
import { EnvironmentService } from "./shared/services/environment.service";
import { AppStateService } from "./shared/services/state/app-state.service";
import { UserStateService } from "./shared/services/state/user-state.service";
import { StorageService } from "./shared/services/storage.service";
import { UsersService } from "./shared/services/users.service";

// To minimize bundle size, only register the modules we use
ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
	selector: "globalictcc-root",
	standalone: true,
	imports: [
		RouterOutlet,
		HeaderComponent,
		SidebarComponent,
		SpinnerComponent,
		ErrorComponent,
		FooterComponent,
		AsyncPipe
	],
	templateUrl: "./app.component.html",
	styleUrl: "./app.component.scss"
})
export class AppComponent implements OnInit, OnDestroy {
	public isLoading: boolean = true;
	public hasError$: Observable<boolean>;
	public dashboardLoading$: Observable<boolean>;

	private _destroy$ = new Subject<void>();

	constructor(
		private _appStateService: AppStateService,
		private _usersService: UsersService,
		private _storageService: StorageService,
		private _environmentService: EnvironmentService,
		private _router: Router,
		private _dialogService: DialogService,
		private _userStateService: UserStateService
	) {}

	ngOnInit() {
		this.hasError$ = this._appStateService.hasError$.pipe(map(hasError => hasError));
		this.dashboardLoading$ = this._appStateService.isDashboardLoading$;

		this._appStateService.setIsAppAccessible(
			this._storageService.getApplicationAccessibilityState()
		);

		this._usersService
			.getUserInfo()
			.pipe(
				catchError(() => this._showErrorDialog()),
				tap({
					next: user => {
						if (!user || this._isUserEmpty(user)) return;

						this._userStateService.setUser(user);

						if (user.role) this._storageService.setUserRole(user.role);

						if (user.snEnabled !== undefined)
							this._storageService.setServiceNowEnabled(user.snEnabled);

						if (user.tenants?.length) this._storageService.setTenants(user.tenants);
					},
					finalize: () => (this.isLoading = false)
				})
			)
			.subscribe();

		this._router.events.pipe(takeUntil(this._destroy$)).subscribe({
			next: event => {
				if (event instanceof NavigationStart) {
					this._environmentService.setBusinessId(uuidv4());
				}
			}
		});
	}

	ngOnDestroy() {
		this._appStateService.setIsAppDestroyed();

		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Checks if the given user object is empty.
	 *
	 * A user is considered empty if it is null, undefined, or if it does not have an `id` property.
	 *
	 * @param user - The user object to check.
	 * @returns `true` if the user is empty, `false` otherwise.
	 */
	private _isUserEmpty(user: UserInfo | null): boolean {
		return !user || user.id === undefined || user.id === null;
	}

	/**
	 * Opens an error dialog to inform the user that there was an error retrieving user information.
	 * The dialog provides an option to retry the action.
	 *
	 * @private
	 * @method _showErrorDialog
	 */
	private _showErrorDialog() {
		this._appStateService.setHasError(true);

		this._dialogService.open({
			data: {
				type: "info",
				title: "There was an error retrieving user information.",
				message: "Please try again later.",
				confirmBtnText: "Retry",
				confirmAction: this._redirect.bind(this)
			}
		});

		return of(null);
	}

	/**
	 * Redirects the user to the unauthorized page.
	 * This method uses the `_environmentService` to perform the redirection.
	 *
	 * @private
	 */
	private _redirect() {
		this._environmentService.unauthorizedRedirect();
	}
}
